import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import VendorLayout from '../../components/VendorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  ShoppingCart, Search, Loader2, Package, Truck, CheckCircle, Clock,
  MapPin, Phone, Mail, ChevronDown, ChevronUp, Box, Download, XCircle,
  Printer, FileText, AlertCircle, IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatPrice } from '../../lib/utils';

export default function VendorOrders() {
  const { vendor, token } = useVendor();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [downloadingLabel, setDownloadingLabel] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Cancel Dialog State
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam) setFilter(statusParam);
  }, [searchParams]);

  useEffect(() => {
    if (vendor && token) {
      fetchOrders();
    }
  }, [vendor, token, filter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get(`${API_URL}/api/vendor/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setOrders(response.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingOrder(orderId);
    try {
      await axios.put(
        `${API_URL}/api/vendor/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Order marked as ${newStatus}`);
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setUpdatingOrder(null);
    }
  };

  const cancelOrder = async () => {
    if (!cancelOrderId || !cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    
    setCancelling(true);
    try {
      await axios.put(
        `${API_URL}/api/vendor/orders/${cancelOrderId}/cancel`,
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order cancelled successfully');
      fetchOrders();
      setCancelDialogOpen(false);
      setCancelOrderId(null);
      setCancelReason('');
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const downloadLabel = async (orderId) => {
    setDownloadingLabel(orderId);
    try {
      const response = await axios.get(
        `${API_URL}/api/shiprocket/label/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.label_url) {
        window.open(response.data.label_url, '_blank');
        toast.success('Label downloaded successfully');
      } else {
        toast.error('Label not available yet. Please try again later.');
      }
    } catch (error) {
      console.error('Error downloading label:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to download label';
      if (errorMsg.includes('not yet synced')) {
        toast.error('Order not yet synced with Shiprocket. Please dispatch the order first.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setDownloadingLabel(null);
    }
  };

  // Print order slip
  const printOrder = (order) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order #${order.id?.slice(-8).toUpperCase()} - NammaHut</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; font-size: 14px; }
            .header { text-align: center; border-bottom: 2px solid #16a34a; padding-bottom: 15px; margin-bottom: 20px; }
            .logo { font-size: 28px; font-weight: bold; color: #16a34a; }
            .tagline { font-size: 12px; color: #666; margin-top: 4px; }
            .order-info { display: flex; justify-content: space-between; margin-bottom: 20px; padding: 15px; background: #f5f5f5; border-radius: 8px; }
            .order-info-item { text-align: center; }
            .order-info-label { font-size: 11px; color: #666; text-transform: uppercase; }
            .order-info-value { font-size: 16px; font-weight: bold; margin-top: 4px; }
            .section { margin-bottom: 20px; }
            .section-title { font-weight: bold; font-size: 16px; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #ddd; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
            .detail-box { padding: 15px; background: #fafafa; border-radius: 8px; }
            .detail-label { font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 4px; }
            .detail-value { font-size: 14px; }
            .items-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            .items-table th { background: #f5f5f5; padding: 10px; text-align: left; font-size: 12px; text-transform: uppercase; }
            .items-table td { padding: 12px 10px; border-bottom: 1px solid #eee; }
            .items-table .qty { text-align: center; }
            .items-table .price { text-align: right; font-weight: 500; }
            .totals { margin-top: 20px; padding: 15px; background: #f5f5f5; border-radius: 8px; }
            .totals-row { display: flex; justify-content: space-between; padding: 8px 0; }
            .totals-row.grand { font-size: 18px; font-weight: bold; color: #16a34a; border-top: 2px solid #16a34a; margin-top: 10px; padding-top: 15px; }
            .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
            .badge-cod { background: #fef3c7; color: #92400e; }
            .badge-paid { background: #d1fae5; color: #065f46; }
            .badge-pending { background: #fef3c7; color: #92400e; }
            .footer { margin-top: 30px; text-align: center; padding-top: 20px; border-top: 1px dashed #ddd; font-size: 12px; color: #666; }
            @media print { 
              body { padding: 10px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">NammaHut</div>
            <div class="tagline">Traditional Indian Marketplace</div>
          </div>
          
          <div class="order-info">
            <div class="order-info-item">
              <div class="order-info-label">Order ID</div>
              <div class="order-info-value">#${order.id?.slice(-8).toUpperCase()}</div>
            </div>
            <div class="order-info-item">
              <div class="order-info-label">Order Date</div>
              <div class="order-info-value">${new Date(order.created_at).toLocaleDateString('en-IN', { dateStyle: 'long' })}</div>
            </div>
            <div class="order-info-item">
              <div class="order-info-label">Status</div>
              <div class="order-info-value"><span class="badge badge-pending">${order.order_status?.toUpperCase() || 'PENDING'}</span></div>
            </div>
            <div class="order-info-item">
              <div class="order-info-label">Payment</div>
              <div class="order-info-value">
                <span class="badge ${order.payment_method === 'COD' ? 'badge-cod' : 'badge-paid'}">
                  ${order.payment_method === 'COD' ? 'COD' : 'PAID'}
                </span>
              </div>
            </div>
          </div>
          
          <div class="grid">
            <div class="section">
              <div class="section-title">Customer Details</div>
              <div class="detail-box">
                <div class="detail-label">Name</div>
                <div class="detail-value">${order.customer_name || order.guest_name || 'Customer'}</div>
                ${order.customer_phone || order.guest_phone || order.shipping_address?.phone ? `
                  <div class="detail-label" style="margin-top: 10px;">Phone</div>
                  <div class="detail-value">${order.customer_phone || order.guest_phone || order.shipping_address?.phone}</div>
                ` : ''}
                ${order.customer_email || order.guest_email ? `
                  <div class="detail-label" style="margin-top: 10px;">Email</div>
                  <div class="detail-value">${order.customer_email || order.guest_email}</div>
                ` : ''}
              </div>
            </div>
            
            <div class="section">
              <div class="section-title">Shipping Address</div>
              <div class="detail-box">
                ${order.shipping_address ? `
                  <div class="detail-value">
                    ${order.shipping_address.street || ''}<br/>
                    ${order.shipping_address.apartment ? order.shipping_address.apartment + '<br/>' : ''}
                    ${order.shipping_address.city || ''}, ${order.shipping_address.state || ''}<br/>
                    PIN: ${order.shipping_address.postal_code || ''}<br/>
                    ${order.shipping_address.country || 'India'}
                  </div>
                ` : '<div class="detail-value">No address provided</div>'}
              </div>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Order Items</div>
            <table class="items-table">
              <thead>
                <tr>
                  <th style="width: 50%">Product</th>
                  <th class="qty" style="width: 15%">Qty</th>
                  <th class="price" style="width: 17%">Unit Price</th>
                  <th class="price" style="width: 18%">Total</th>
                </tr>
              </thead>
              <tbody>
                ${order.items?.map(item => `
                  <tr>
                    <td>${item.name}${item.variant ? ` (${item.variant})` : ''}</td>
                    <td class="qty">${item.quantity}</td>
                    <td class="price">${formatPrice(item.price)}</td>
                    <td class="price">${formatPrice(item.price * item.quantity)}</td>
                  </tr>
                `).join('') || '<tr><td colspan="4">No items</td></tr>'}
              </tbody>
            </table>
          </div>
          
          <div class="totals">
            <div class="totals-row">
              <span>Subtotal</span>
              <span>${formatPrice(order.subtotal || 0)}</span>
            </div>
            <div class="totals-row">
              <span>Shipping</span>
              <span>${formatPrice(order.shipping_cost || 0)}</span>
            </div>
            ${order.discount ? `
              <div class="totals-row">
                <span>Discount</span>
                <span>-${formatPrice(order.discount)}</span>
              </div>
            ` : ''}
            <div class="totals-row grand">
              <span>Grand Total</span>
              <span>${formatPrice(order.total || 0)}</span>
            </div>
          </div>
          
          <div class="footer">
            <p>Thank you for your order!</p>
            <p style="margin-top: 5px;">For queries, contact support@nammahut.com</p>
          </div>
          
          <div class="no-print" style="margin-top: 20px; text-align: center;">
            <button onclick="window.print()" style="padding: 10px 30px; background: #16a34a; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 16px;">
              Print Order
            </button>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, label: 'Pending' },
      processing: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Package, label: 'Processing' },
      packed: { color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Box, label: 'Packed' },
      dispatched: { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Truck, label: 'Dispatched' },
      shipped: { color: 'bg-cyan-100 text-cyan-800 border-cyan-200', icon: Truck, label: 'Shipped' },
      delivered: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'Delivered' },
      cancelled: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, label: 'Cancelled' }
    };
    return configs[status] || configs.pending;
  };

  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      order.id?.toLowerCase().includes(search) ||
      order.guest_name?.toLowerCase().includes(search) ||
      order.guest_phone?.includes(search)
    );
  });

  const statusFilters = [
    { value: 'all', label: 'All Orders' },
    { value: 'pending', label: 'Pending' },
    { value: 'packed', label: 'Packed' },
    { value: 'dispatched', label: 'Dispatched' },
    { value: 'delivered', label: 'Delivered' }
  ];

  return (
    <VendorLayout>
      <div className="space-y-6" data-testid="vendor-orders-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
            <p className="text-gray-500 text-sm">{orders.length} total orders</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {statusFilters.map(f => (
            <Button
              key={f.value}
              variant={filter === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f.value)}
              className={filter === f.value ? 'bg-green-600 hover:bg-green-700' : ''}
              data-testid={`filter-${f.value}`}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search by order ID, name, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="order-search"
          />
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
              <p className="text-gray-500">Orders will appear here once customers place them</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => {
              const statusConfig = getStatusConfig(order.order_status);
              const StatusIcon = statusConfig.icon;
              const isExpanded = expandedOrder === order.id;
              const isCOD = order.payment_method === 'COD';
              
              return (
                <Card key={order.id} className="overflow-hidden" data-testid={`order-card-${order.id}`}>
                  <CardContent className="p-0">
                    {/* Order Header */}
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${statusConfig.color}`}>
                          <StatusIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            Order #{order.id?.slice(-8).toUpperCase()}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(order.created_at).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-green-600">{formatPrice(order.total)}</p>
                          <p className="text-sm text-gray-500">{order.items?.length || 0} items</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Badge className={`${statusConfig.color} border`}>
                            {statusConfig.label}
                          </Badge>
                          {isCOD && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-xs">
                              COD
                            </Badge>
                          )}
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t bg-gray-50 p-4">
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Customer Info */}
                          <div className="space-y-3">
                            <h4 className="font-medium text-gray-700">Customer Details</h4>
                            <div className="space-y-2 text-sm bg-white p-3 rounded-lg">
                              <p className="flex items-center gap-2 text-gray-900 font-medium">
                                {order.customer_name || order.guest_name || 'Customer'}
                              </p>
                              {(order.customer_phone || order.guest_phone || order.shipping_address?.phone) && (
                                <p className="flex items-center gap-2 text-gray-600">
                                  <Phone className="w-4 h-4 text-gray-400" />
                                  {order.customer_phone || order.guest_phone || order.shipping_address?.phone}
                                </p>
                              )}
                              {(order.customer_email || order.guest_email) && (
                                <p className="flex items-center gap-2 text-gray-600">
                                  <Mail className="w-4 h-4 text-gray-400" />
                                  {order.customer_email || order.guest_email}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Shipping Address */}
                          <div className="space-y-3">
                            <h4 className="font-medium text-gray-700">Shipping Address</h4>
                            {order.shipping_address && (
                              <div className="text-sm text-gray-600 bg-white p-3 rounded-lg">
                                <p className="flex items-start gap-2">
                                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                  <span>
                                    {order.shipping_address.street}
                                    {order.shipping_address.apartment && `, ${order.shipping_address.apartment}`}<br/>
                                    {order.shipping_address.city}, {order.shipping_address.state}<br/>
                                    PIN: {order.shipping_address.postal_code}
                                  </span>
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Payment Info */}
                        <div className="mt-4 p-3 bg-white rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <IndianRupee className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">Payment Method:</span>
                            </div>
                            <Badge className={`${isCOD ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                              {isCOD ? 'Cash on Delivery' : 'Paid Online'}
                            </Badge>
                          </div>
                        </div>

                        {/* Order Items */}
                        <div className="mt-6">
                          <h4 className="font-medium text-gray-700 mb-3">Order Items</h4>
                          <div className="space-y-2">
                            {order.items?.map((item, index) => (
                              <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                                <div className="flex items-center gap-3">
                                  {item.image ? (
                                    <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                                  ) : (
                                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                      <Package className="w-6 h-6 text-gray-400" />
                                    </div>
                                  )}
                                  <div>
                                    <p className="font-medium text-gray-900">{item.name}</p>
                                    <p className="text-sm text-gray-500">Qty: {item.quantity} × {formatPrice(item.price)}</p>
                                  </div>
                                </div>
                                <p className="font-semibold">{formatPrice(item.price * item.quantity)}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Order Summary */}
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex justify-end">
                            <div className="w-64 space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Subtotal</span>
                                <span>{formatPrice(order.subtotal)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Shipping</span>
                                <span>{formatPrice(order.shipping_cost || 0)}</span>
                              </div>
                              <div className="flex justify-between font-bold text-base pt-2 border-t">
                                <span>Total</span>
                                <span className="text-green-600">{formatPrice(order.total)}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-6 flex flex-wrap gap-2">
                          {/* Status Update Buttons */}
                          {order.order_status === 'pending' && (
                            <>
                              <Button
                                onClick={() => updateOrderStatus(order.id, 'packed')}
                                disabled={updatingOrder === order.id}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid={`pack-btn-${order.id}`}
                              >
                                {updatingOrder === order.id ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Box className="w-4 h-4 mr-2" />
                                )}
                                Accept & Pack
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setCancelOrderId(order.id);
                                  setCancelDialogOpen(true);
                                }}
                                className="border-red-200 text-red-700 hover:bg-red-50"
                                data-testid={`cancel-btn-${order.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Cancel Order
                              </Button>
                            </>
                          )}
                          
                          {order.order_status === 'packed' && (
                            <Button
                              onClick={() => updateOrderStatus(order.id, 'dispatched')}
                              disabled={updatingOrder === order.id}
                              className="bg-purple-600 hover:bg-purple-700"
                              data-testid={`dispatch-btn-${order.id}`}
                            >
                              {updatingOrder === order.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Truck className="w-4 h-4 mr-2" />
                              )}
                              Mark as Dispatched
                            </Button>
                          )}

                          {order.order_status === 'dispatched' && (
                            <Button
                              onClick={() => updateOrderStatus(order.id, 'delivered')}
                              disabled={updatingOrder === order.id}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`deliver-btn-${order.id}`}
                            >
                              {updatingOrder === order.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4 mr-2" />
                              )}
                              Mark as Delivered
                            </Button>
                          )}

                          {/* Print Order Button - Always Available */}
                          <Button
                            variant="outline"
                            onClick={() => printOrder(order)}
                            className="border-gray-300"
                            data-testid={`print-btn-${order.id}`}
                          >
                            <Printer className="w-4 h-4 mr-2" />
                            Print Order
                          </Button>

                          {/* Download AWB/Label Button */}
                          {(order.order_status === 'packed' || order.order_status === 'dispatched' || order.order_status === 'shipped') && (
                            <Button
                              variant="outline"
                              onClick={() => downloadLabel(order.id)}
                              disabled={downloadingLabel === order.id}
                              className="border-green-200 text-green-700 hover:bg-green-50"
                              data-testid={`download-label-btn-${order.id}`}
                            >
                              {downloadingLabel === order.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4 mr-2" />
                              )}
                              Download AWB
                            </Button>
                          )}

                          {/* Shiprocket Info Badges */}
                          {order.shiprocket_order_id && (
                            <Badge className="bg-green-100 text-green-800">
                              Shiprocket: #{order.shiprocket_order_id}
                            </Badge>
                          )}

                          {order.awb_code && (
                            <Badge className="bg-blue-100 text-blue-800">
                              AWB: {order.awb_code}
                            </Badge>
                          )}

                          {/* Cancellation Reason */}
                          {order.order_status === 'cancelled' && order.cancel_reason && (
                            <div className="w-full mt-2 p-3 bg-red-50 rounded-lg border border-red-200">
                              <p className="text-sm text-red-800">
                                <AlertCircle className="w-4 h-4 inline mr-1" />
                                <strong>Cancellation Reason:</strong> {order.cancel_reason}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Cancel Order Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Cancel Order
              </DialogTitle>
              <DialogDescription>
                Please provide a reason for cancelling this order. The customer will be notified.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Textarea
                placeholder="Enter cancellation reason (e.g., Out of stock, Customer requested cancellation, etc.)"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
                className="w-full"
                data-testid="cancel-reason-input"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setCancelDialogOpen(false);
                  setCancelOrderId(null);
                  setCancelReason('');
                }}
              >
                Go Back
              </Button>
              <Button
                variant="destructive"
                onClick={cancelOrder}
                disabled={cancelling || !cancelReason.trim()}
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                Confirm Cancellation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </VendorLayout>
  );
}
